import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, orderBy, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './GrnPage.css';
import ViewGrnModal from './ViewGrnModal';

const GoodsReceivedNotesPage = () => {
    const [grns, setGrns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedGrn, setSelectedGrn] = useState(null);

    useEffect(() => {
        fetchGrns();
    }, []);

    const fetchGrns = async () => {
        try {
            const grnsCollectionRef = collection(db, "grns");
            // Sorting descending ensures the newest items are first in the array
            const q = query(grnsCollectionRef, orderBy("createdAt", "desc"));
            const data = await getDocs(q);
            setGrns(data.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
        } catch (error) {
            console.error("Error fetching GRNs: ", error);
        } finally {
            setLoading(false);
        }
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(price);
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp || typeof timestamp.toDate !== 'function') {
            return 'N/A';
        }
        return new Date(timestamp.toDate()).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };
    
    // --- NEW: Function to format the display ID ---
    /**
     * Creates a user-friendly, sequential ID for display.
     * @param {number} index - The index of the item in the DESC-sorted array.
     * @param {number} totalItems - The total number of items in the array.
     * @returns {string} The formatted ID, e.g., "GRN_0001".
     */
    const formatDisplayId = (index, totalItems) => {
        // Because the array is sorted newest-first, we subtract the index 
        // from the total length to get the correct chronological number.
        const sequentialNumber = totalItems - index;
        // Pad the number with leading zeros to ensure a 4-digit length
        const paddedNumber = String(sequentialNumber).padStart(4, '0');
        return `GRN_${paddedNumber}`;
    };


    const handleView = (grn) => {
        // We can add the displayId to the selectedGrn object if needed in the modal
        const grnWithDisplayId = {
            ...grn,
            displayId: formatDisplayId(grns.findIndex(g => g.id === grn.id), grns.length)
        };
        setSelectedGrn(grnWithDisplayId);
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        const confirmDelete = window.confirm("Are you sure you want to delete this GRN? This action cannot be undone.");

        if (confirmDelete) {
            try {
                const grnDoc = doc(db, "grns", id);
                await deleteDoc(grnDoc);
                setGrns(grns.filter((grn) => grn.id !== id));
                alert("GRN deleted successfully.");
            } catch (error) {
                console.error("Error deleting GRN: ", error);
                alert("Failed to delete GRN. Check the console for more details.");
            }
        }
    };

    return (
        <>
            <div className="list-container-grn">
                <div className="list-header-grn">
                    <h1>Goods Received Notes</h1>
                    <Link to="/grns/add" className="add-grn-button">Add New GRN</Link>
                </div>
                {loading ? <p>Loading...</p> :
                <table className="grn-table">
                    <thead>
                        <tr>
                            <th>GRN ID</th>
                            <th>Purchase Date</th>
                            <th>Created At</th>
                            <th>Received By</th>
                            <th>Total Price</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {grns.length === 0 ? (
                            <tr><td colSpan="6" style={{ textAlign: 'center' }}>No GRNs found.</td></tr>
                        ) : (
                            // Pass the index to the map function
                            grns.map((grn, index) => (
                                <tr className="grn-main-row" key={grn.id}>
                                    {/* --- UPDATED: Use the new formatDisplayId function --- */}
                                    {/* We pass the index and the total length of the grns array */}
                                    <td className="grn-id-cell">{formatDisplayId(index, grns.length)}</td>
                                    <td>{grn.purchaseDate}</td>
                                    <td>{formatTimestamp(grn.createdAt)}</td>
                                    <td>{grn.addedBy}</td>
                                    <td>{formatPrice(grn.totalPurchasePrice)}</td>
                                    <td className="actions-cell">
                                        <button onClick={() => handleView(grn)} className="action-button view">View</button>
                                        <button onClick={() => handleDelete(grn.id)} className="action-button delete">Delete</button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>}
            </div>

            {isModalOpen && selectedGrn && (
                <ViewGrnModal
                    grn={selectedGrn}
                    onClose={() => setIsModalOpen(false)}
                />
            )}
        </>
    );
};

export default GoodsReceivedNotesPage;